import { STANDARD_BRANCH } from '@festina-life/api'
import { assertNotEmpty, dateUtils } from '@festina-life/core'
import { Group } from '@visx/group'

import { useDraftContext } from '../../context/draftContext'
import { useTemplateContext } from '../../context/templateContext'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useIsPersonaEditor } from '../../uiModels/useIsPersonaEditor'
import { useInheritedTemplateVersionInfos } from '../../uiModels/versionInfo/useInheritedTemplateVersionInfos'
import { useTemplateVersionInfos } from '../../uiModels/versionInfo/useTemplateVersionInfo'
import { NO_DATE_SYMBOL } from '../../util/constants'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { ARROW_LINE_WIDTH } from '../versionedTimeline/constants'
import { getHiddenTimeIntervals } from '../versionedTimeline/helpers'
import { VersionedTimeline } from '../versionedTimeline/VersionedTimeline'
import { TemplateVersionsForDate } from './TemplateVersionsForDate'

type Props = {
  /** Identifier of the template. */
  templateId: string
  /** Top margin (y-position). */
  top: number
  /** Length of the line in pixels */
  lengthInPixels: number
  /** Start date represented by the line */
  startDate: Date
  /** End date represented by the line */
  endDate: Date
}

export const TemplateVersionsLine = ({
  templateId,
  top,
  lengthInPixels,
  startDate,
  endDate,
}: Props) => {
  const { templateVersion } = useTemplateContext()
  const { applicationTemplates } = useApplicationViewOperationsContext()
  const { onConfigureNewDraft } = useDraftContext()
  const { branch } = useNavigationParams()
  const versions = useTemplateVersionInfos(templateId)
  const isEditor = useIsPersonaEditor()

  const inheritedVersions = useInheritedTemplateVersionInfos(templateId)

  const hiddenTimeIntervals = getHiddenTimeIntervals({
    items: versions,
    getFromDate: (version) => version.fromDate,
    isHideItem: (version) => version.isHideVersion,
    endDate,
  })

  const allVersions = [
    ...inheritedVersions.filter(
      (inheritedVersion) =>
        !hiddenTimeIntervals.some((interval) => {
          const versionFromDateTime = inheritedVersion.fromDate
            ? dateUtils.dateUtcAsLocal(inheritedVersion.fromDate).valueOf()
            : Number.NEGATIVE_INFINITY
          const intervalFromDateTime = interval[0]
            ? dateUtils.dateUtcAsLocal(interval[0]).valueOf()
            : Number.NEGATIVE_INFINITY
          const intervalToDateTime = dateUtils
            .dateUtcAsLocal(interval[1])
            .valueOf()

          return (
            versionFromDateTime >= intervalFromDateTime &&
            versionFromDateTime <= intervalToDateTime
          )
        }),
    ),
    ...versions,
  ]

  const handleCreateDraft = (fromDate: Date) => {
    assertNotEmpty(templateVersion)

    const currentTemplateModelTypeName =
      templateVersion?.modelTypeName ??
      applicationTemplates?.get(templateVersion?.versionInfo.identifier ?? '')
        ?.lastEntity?.modelTypeName ??
      ''
    const currentTemplatePackagePath = templateVersion?.versionInfo.package
      ?.length
      ? templateVersion.versionInfo.package
      : (applicationTemplates
          ?.get(templateVersion?.versionInfo.identifier ?? '')
          ?.lastEntity?.packageName?.split('.') ?? [])

    onConfigureNewDraft(
      {
        type: 'template',
        value: {
          packagePath: currentTemplatePackagePath,
          templateId,
          fromDate: dateUtils.dateAsServerStr(fromDate),
          modelTypeName: currentTemplateModelTypeName,
          isHideOperation: false,
          variant: 'version',
        },
      },
      templateVersion.layer,
    )
  }

  const isCurrentTemplateId =
    templateId === templateVersion?.versionInfo.identifier

  return (
    <Group top={top - ARROW_LINE_WIDTH / 2}>
      <VersionedTimeline
        lengthInPixels={lengthInPixels}
        startDate={startDate}
        endDate={endDate}
        // It's important to place inherited versions first so that versions are ordered from least relevant to most relevant.
        // Ie. a version defined on the current templateId is more relevant than one coming from a parent. And one coming from a direct parent
        // is more relevant than one coming from an ancestor. This is used in TemplateVersionsForDate to always select the last version in the array
        // when a single version circle is shown.
        versions={allVersions}
        onCreateDraft={
          branch &&
          branch !== STANDARD_BRANCH &&
          isEditor &&
          isCurrentTemplateId
            ? handleCreateDraft
            : undefined
        }
        versionDate={(version) => version.fromDate ?? NO_DATE_SYMBOL}
        renderDateVersions={({ date, versions, dateToPixel }) => (
          <TemplateVersionsForDate
            key={date.toString()}
            versions={versions}
            dateToPixel={dateToPixel}
            isInherited={(version) => inheritedVersions.includes(version)}
            currentVersionLineId={templateId}
            earliestVersionDate={startDate}
          />
        )}
        hiddenTimeIntervals={hiddenTimeIntervals}
      />
    </Group>
  )
}
